import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Sparkles, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * VehicleLeadForm — capture email sur la fiche véhicule.
 * Modes :
 *  - "available" : véhicule disponible → alerte baisse de prix / modèle similaire
 *  - "sold"      : véhicule vendu → recherche similaire (email + texte libre)
 *  - "confidential" : prix confidentiel → recevoir le prix par email
 */
export default function VehicleLeadForm({ vehicleId, mode = "available", brand = "", model = "" }) {
  const [email, setEmail] = useState("");
  const [recherche, setRecherche] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const config = {
    available: {
      title: "Ce véhicule vous intéresse ?",
      text: "Laissez-nous votre email pour être prévenu en priorité en cas de baisse de prix, ou dès qu'un modèle similaire arrive en stock.",
      cta: "Être alerté",
      icon: Sparkles,
      source: "vehicle_alert",
      showRecherche: false,
    },
    sold: {
      title: "Ce modèle a trouvé preneur.",
      text: "Vous cherchez la même chose ? Laissez-nous vos critères, on vous prévient dès qu'un véhicule similaire arrive.",
      cta: "Me prévenir",
      icon: Mail,
      source: "similar_search",
      showRecherche: true,
      recherchePlaceholder: `Modèle recherché (ex : ${brand || "Porsche"} ${model || "911"} phase 2, boîte manuelle, moins de 60 000 km…)`,
    },
    confidential: {
      title: "Prix sur demande — pièce rare",
      text: "Ce véhicule fait l'objet d'un tarif confidentiel. Recevez le prix par email en quelques secondes.",
      cta: "Recevoir le prix par email",
      icon: ShieldCheck,
      source: "confidential_price",
      showRecherche: false,
    },
  };

  const cfg = config[mode] || config.available;
  const Icon = cfg.icon;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", {
        email,
        vehicle_id: vehicleId || null,
        vehicle_recherche: recherche || "",
        source: cfg.source,
      });
      setSent(true);
      setEmail("");
      setRecherche("");
      toast.success("Merci — nous vous recontactons rapidement.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur — réessayez.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid={`lead-form-success-${mode}`}
        className="bg-[#1E1E1E] border border-[#C8A75A]/40 rounded-md p-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <span className="w-9 h-9 rounded-full bg-[#C8A75A]/15 flex items-center justify-center text-[#C8A75A]">✓</span>
          <p className="font-display text-xl text-white">Bien noté, merci !</p>
        </div>
        <p className="text-white/60 text-sm leading-relaxed">
          Votre demande est enregistrée. Vous recevrez un email dès que nous avons du nouveau à vous partager.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid={`lead-form-${mode}`}
      onSubmit={submit}
      className="bg-[#1E1E1E] border border-[#C8A75A]/25 rounded-md p-6 space-y-4"
    >
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-full bg-[#C8A75A]/15 flex items-center justify-center text-[#C8A75A] flex-shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <div>
          <p className="font-display text-xl text-white leading-tight">{cfg.title}</p>
          <p className="text-white/60 text-sm leading-relaxed mt-2">{cfg.text}</p>
        </div>
      </div>

      <input
        data-testid={`lead-form-${mode}-email`}
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="votre@email.com"
        className="w-full bg-[#0D0D0D] border border-white/10 focus:border-[#C8A75A]/60 outline-none rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30"
      />

      {cfg.showRecherche && (
        <textarea
          data-testid={`lead-form-${mode}-recherche`}
          rows={3}
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder={cfg.recherchePlaceholder}
          className="w-full bg-[#0D0D0D] border border-white/10 focus:border-[#C8A75A]/60 outline-none rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 resize-none"
        />
      )}

      <button
        type="submit"
        disabled={sending}
        data-testid={`lead-form-${mode}-submit`}
        className="w-full bg-[#C8A75A] text-black hover:bg-white transition-colors font-semibold px-6 py-3.5 rounded-md text-xs uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {sending ? "Envoi…" : cfg.cta}
        {!sending && <Send className="w-4 h-4" />}
      </button>

      <p className="text-white/40 text-[11px] leading-relaxed text-center">
        Aucun spam, désinscription en un clic.
      </p>
    </form>
  );
}
